// @ts-check
/** @typedef {import('./types/charts.types').TwoLineDonutProps} TwoLineDonutProps */

// Replacement for Tremor DonutChart that uses the underlying Recharts library to enable two line labels in the middle of the chart
import { ResponsiveContainer, PieChart, Pie, Cell, Label, Tooltip as RechartsTooltip, } from "recharts";
import { resolveTokenToColour } from "@datahooks/metadata/ColourUtils";
import { TREMOR_COLOUR_NAMES, clampToRange } from "@datahooks/metadata/DataUtils";

import PropTypes from "prop-types";

/** Tooltip component
 * @param {{
 *    active?: boolean, 
 *    payload?: any[]
 *  }} props
 */
function TwoLineDonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;

  const tooltipData = payload[0];

  const value = Number(tooltipData.value) || 0;

  return (
    <div className="rounded-tremor-default text-tremor-default border bg-tremor-background shadow-tremor-dropdown border-tremor-border dark:bg-dark-tremor-background 
    dark:shadow-dark-tremor-dropdown dark:border-dark-tremor-border px-3 py-2 border-gray-200 text-gray-900 dark:text-gray-100 min-w-[140px]">
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: tooltipData.payload.fill }}/>
        <span className="text-sm">{tooltipData.name}</span>
        <span className="ml-auto text-sm font-medium tabular-nums">{value}</span>
      </div>
    </div>
  );
}

TwoLineDonutTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

/**
 * Multi line label component
 *
 * @param {{
 *  topText: string,
 *  bottomText: string,
 *  viewBox: object
 * }} props
 */
function TwoLineDonutLabels({ topText, bottomText, viewBox }) {
  if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) return null;
   
  const { cx, cy, width, height, outerRadius: viewboxOuter } = /** @type {any} */ (viewBox);

  // Derive radius
  let radius;
  if (typeof viewboxOuter === 'number') {
    radius = viewboxOuter;
  } else {
    radius = (width && height ? Math.min(width, height) / 2 : 0);
  }

  if (!radius) return null;

  const diameter = radius * 2;
  const hideBottom = diameter < 90; // pixels

  // scale fonts with radius (with sensible floors/ceilings)
  const topTextSize = clampToRange(Math.round(radius * 0.44), 12, 36);
  const bottomTextSize = clampToRange(Math.round(radius * 0.18), 10, 16);
  const topPos = hideBottom ? "0em" : "-0.4em";

  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central">
        <tspan x={cx} dy={topPos} fontSize={topTextSize} fontWeight="500" fill='#1D3154'>{topText}</tspan>
        {hideBottom ? null : <tspan x={cx} dy="2em" fontSize={bottomTextSize} opacity="0.7" fontWeight="700" fill='#1D3154'>{bottomText}</tspan>}
    </text>
  );
}

TwoLineDonutLabels.propTypes = {
  topText: PropTypes.string,
  bottomText: PropTypes.string,
  viewBox: PropTypes.shape({
    cx: PropTypes.number,
    cy: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    outerRadius: PropTypes.number, // Recharts provides number here for Pie rather than percentage
  }),
};

/** @param {TwoLineDonutProps} props */
function TwoLineDonut({
  data,
  colours = ['green', 'pink'],
  topText,
  bottomText,
  innerRadius = "70%",
  outerRadius = "92%",
  className
}) {
  const chartData = data.map(dataRow => ({
    name: dataRow['name'],
    value: Number(dataRow['count']) || 0,
  }));

  const fills = colours.map(colour => resolveTokenToColour(colour));

  return (
    <div className={className} style={{ width: "100%", aspectRatio: "1/1", maxWidth: "12rem" }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie startAngle={90} endAngle={-270} data={chartData} dataKey="value" nameKey="name" innerRadius={innerRadius} outerRadius={outerRadius} isAnimationActive>
            {chartData.map((chartRow, i) => (
              <Cell key={chartRow.name} fill={fills[i % fills.length]} />
            ))}

            <Label
                position="center"
                content={<TwoLineDonutLabels topText={topText} bottomText={bottomText} />}
            />

          </Pie>
          {/* Tremor-style tooltip */}
          <RechartsTooltip
            wrapperStyle={{ outline: "none" }}
            cursor={{ fill: "transparent" }}
            content={<TwoLineDonutTooltip />}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

TwoLineDonut.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  })).isRequired,
  colours: PropTypes.arrayOf(PropTypes.oneOf(TREMOR_COLOUR_NAMES)),
  topText: PropTypes.string,
  bottomText: PropTypes.string,
  innerRadius: PropTypes.string,
  outerRadius: PropTypes.string,
  className: PropTypes.string
};

export default TwoLineDonut;